/** @module Auth0SsoButtonHelper */
/* eslint-disable import/extensions */
import Auth0UtilsBrowser from './auth0-utils-browser.mjs';
import Auth0SupportedIdps from './auth0-supported-idps.mjs';
import Auth0Localizer from './auth0-localization-helper.mjs';

/**
 * @constructor
 * @description Helper functions related to rendering styled and placed SSO buttons in the standard way (or some helper functions to allow for custom button layout)
 * @param {object} options Button helper init options
 * @param {string|object} options.tenantOrDomain The name or object representation of the Auth0 tenant or domain fow which to render buttons
 * @param {string} [options.appLoginHandlerUrl] The absolute URL to the auth handler for client application (v2, v3, etc)
 */
class Auth0SsoButtonHelper {
  constructor(options) {
    const resolved = Auth0UtilsBrowser.tenantOrDomainResolver(options.tenantOrDomain);
    this._domain = resolved.domain;
    this._tenant = resolved.tenant;
    this._idpHelper = new Auth0SupportedIdps(options);
    this._appLoginHandlerUrl = options.appLoginHandlerUrl;
  }

  /**
   * @function
   * @description A helper function that wil generate the SSO button list and then inject them into the user-specified container
   * @param {object} options Button generation options
   * @param {HTMLElement|string} options.container The container into which the SSO buttons should be injected. Can be an HTMLElement or container ID.
   * @param {string} options.buttonTextKey The key to the localizable string resource that will be used for the button's text
   * @param {function} options.buttonClickHandler The function that will be called, passing the IdP ID and provided appLoginHandlerUrl, when it is clicked
   * @param {array} [options.buttonClassList] The optional list of classes to apply to each of the SSO buttons
   */
  injectSsoButtonSet = (options) => {
    const containerElem = Auth0UtilsBrowser.isString(options.container)
      ? document.getElementById(options.container)
      : options.container;

    this._idpHelper.activeIdps.forEach((idp) => {
      containerElem.appendChild(this.generateSsoButton(idp, options));
    });
  };

  /**
   * @function
   * @description A helper function that injects only the Step 1 (Always Available) SSO buttons into the user-specified container.
   * These buttons will be shown on Step 1 of progressive auth alongside the username field.
   * @param {object} options Button generation options
   * @param {HTMLElement|string} options.container The container into which the SSO buttons should be injected. Can be an HTMLElement or container ID.
   * @param {string} options.buttonTextKey The key to the localizable string resource that will be used for the button's text
   * @param {function} options.buttonClickHandler The function that will be called, passing the IdP ID and provided appLoginHandlerUrl, when it is clicked
   * @param {array} [options.buttonClassList] The optional list of classes to apply to each of the SSO buttons
   * @returns {number} The number of Step 1 (Always Available) SSO buttons injected
   */
  injectAlwaysAvailableSsoButtonSet = (options) => {
    const containerElem = Auth0UtilsBrowser.isString(options.container)
      ? document.getElementById(options.container)
      : options.container;

    const { alwaysAvailableIdps } = this._idpHelper;
    alwaysAvailableIdps.forEach((idp) => {
      containerElem.appendChild(this.generateSsoButton(idp, options));
    });

    return alwaysAvailableIdps.length;
  };

  /**
   * @function
   * @description A helper function that injects SSO buttons that are NOT in the Step 1 (Always Available) list.
   * Used in Step 2 to add additional SSO buttons as dictated by SSAS, excluding those already shown in Step 1.
   * @param {object} options Button generation options
   * @param {HTMLElement|string} options.container The container into which the SSO buttons should be injected. Can be an HTMLElement or container ID.
   * @param {string} options.buttonTextKey The key to the localizable string resource that will be used for the button's text
   * @param {function} options.buttonClickHandler The function that will be called, passing the IdP ID and provided appLoginHandlerUrl, when it is clicked
   * @param {array} [options.buttonClassList] The optional list of classes to apply to each of the SSO buttons
   * @param {array} [options.additionalIdps] Optional array of IdP IDs to add on top of always-available IDPs (if present, these specific IDPs will be added)
   * @returns {number} The number of Step 2 additional SSO buttons injected
   */
  injectAdditionalSsoButtonSet = (options) => {
    const containerElem = Auth0UtilsBrowser.isString(options.container)
      ? document.getElementById(options.container)
      : options.container;

    const alwaysAvailableIds = this._idpHelper.alwaysAvailableIdps.map(idp => idp.id);
    let idpsToInject = this._idpHelper.activeIdps.filter(idp => !alwaysAvailableIds.includes(idp.id));

    // If additionalIdps specified, only inject those (that are not already always-available)
    if (options.additionalIdps && options.additionalIdps.length > 0) {
      idpsToInject = idpsToInject.filter(idp => options.additionalIdps.includes(idp.id));
    }

    idpsToInject.forEach((idp) => {
      containerElem.appendChild(this.generateSsoButton(idp, options));
    });

    return idpsToInject.length;
  };

  /**
   * @function
   * @description A helper function that wil attempt to locate and return an array of HTMLElements representing the IdP SSO buttons
   * @returns {Array} An array of found SSO buttons (HTMLElement)
   */
  getCurrentSsoButtonSet = () => {
    const foundSsoButtons = [];
    this._idpHelper.activeIdps.forEach((idp) => {
      const ele = document.getElementById(this._generateButtonId(idp));
      if (ele) {
        foundSsoButtons.push(ele);
      }
    });

    return foundSsoButtons;
  };

  /**
   * @function
   * @description A helper function that wil generate the SSO button list within a single node (div) container
   * @param {object} options Button generation options
   * @param {string} options.buttonTextKey The key to the localizable string resource that will be used for the button's text
   * @param {function} options.buttonClickHandler The function that will be called, passing the IdP ID and provided appLoginHandlerUrl, when it is clicked
   * @param {string} [options.containerId] The optional ID of the div container
   * @param {array} [options.classList] The optional list of classes to apply to the div container
   * @param {array} [options.buttonClassList] The optional list of classes to apply to each of the SSO buttons
   * @param {boolean} [options.generateAsHtmlText=false] Optional. If false (default), this function will return a standard `HTMLElement` object containing the buttons. Otherwise, it will return the equivalent HTML text
   * @returns {HTMLElement|string} The single node (div) containing the SSO buttons. This object has not been added to the DOM.
   */
  generateSsoButtonSet = (options) => {
    const container = document.createElement('div');
    if (options.containerId) {
      container.id = options.containerId;
    }
    if (Auth0UtilsBrowser.isArray(options.classList)) {
      options.classList.forEach(clas => container.classList.add(clas));
    }

    this.injectSsoButtonSet({
      ...options,
      container,
    });

    return options.generateAsHtmlText
      ? container.outerHTML
      : container;
  };

  /**
   * @function
   * @description A helper function that wil generate a single SSO button for the provided IdP
   * @param {object|string} socialIdp The IdP for which to generate a button
   * @param {object} options Button generation options
   * @param {string} options.buttonTextKey The key to the localizable string resource that will be used for the button's text
   * @param {function} options.buttonClickHandler The function that will be called, passing the IdP ID and provided appLoginHandlerUrl, when it is clicked
   * @param {array} [options.buttonClassList] The optional list of classes to apply to the SSO button
   * @param {boolean} [options.generateAsHtmlText=false] Optional. If false (default), this function will return a standard `HTMLElement` object containing the button. Otherwise, it will return the equivalent HTML text
   * @returns {HTMLElement|string} The single node (div) containing the SSO button for the specified IDP. This object has not been added to the DOM.
   */
  generateSsoButton = (socialIdp, options) => {
    const ssoButton = document.createElement('div');
    socialIdp = Auth0UtilsBrowser.isString(socialIdp)
      ? this._idpHelper.getIdpById(socialIdp)
      : socialIdp;
    ssoButton.id = this._generateButtonId(socialIdp);
    ssoButton.innerHTML = `<img src="${socialIdp.logoUrl}" /><span data-translatable-element="true" data-resource-id="${options.buttonTextKey}" data-localize-args="{&quot;idpName&quot;:&quot;${socialIdp.name}&quot;}">${Auth0Localizer.getLocalizedText(options.buttonTextKey, { idpName: socialIdp.name })}</span>`;
    ssoButton.onclick = () => { options.buttonClickHandler(socialIdp.id, this._appLoginHandlerUrl); };
    if (Auth0UtilsBrowser.isArray(options.buttonClassList)) {
      options.buttonClassList.forEach(clas => ssoButton.classList.add(clas));
    }
    return options.generateAsHtmlText
      ? ssoButton.outerHTML
      : ssoButton;
  };

  _generateButtonId = (socialIdp) => {
    return `sso-${socialIdp.id}`;
  };
}

export default Auth0SsoButtonHelper;
